import { useMemo, ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import { DataTable, Column } from 'src/components/UI/DataTable';
import CloseIcon from '@mui/icons-material/Close';
import {
  campaignOrderContainerSx,
  campaignOrderTitleSx,
  totalContainerSx,
  totalLabelSx,
  totalPriceValueSx,
  buttonsContainerSx,
  orderedItemTitleSx,
  orderedPriceContainerSx,
  orderedPriceSx,
  closeIconSx
} from '../styles/createCampaign';

export type OrderedData = {
  id: number;
  title: string;
  price: number;
  onRemove?: (id: number) => void;
};

export type CampaignOrderProps = {
  data: OrderedData[];
  actions?: ReactNode[];
  title?: string;
};

export const CampaignOrder = ({
  data,
  actions,
  title = 'Campaign Order'
}: CampaignOrderProps) => {
  const total = useMemo(() => {
    const sum = data.reduce((acc, item) => {
      return acc + (item.price || 0);
    }, 0);
    // Format as currency
    return `$${sum.toFixed(2)}`;
  }, [data]);

  const orderedColumns: Column<OrderedData>[] = useMemo(
    () => [
      {
        id: 'title',
        label: 'Item',
        width: 100,
        render: (row) => (
          <Typography sx={orderedItemTitleSx}>{row.title}</Typography>
        )
      },
      {
        id: 'price',
        label: 'Price',
        width: 100,
        render: (row) => (
          <Box sx={orderedPriceContainerSx}>
              <Typography sx={orderedPriceSx}>
                ${row.price.toFixed(2)}
              </Typography>
              {row.onRemove && (
                <CloseIcon sx={closeIconSx} onClick={() => row.onRemove(row.id)} />
              )}
            </Box>
        )
      }
    ],
    []
  );

  return (
    <Box sx={campaignOrderContainerSx}>
      <Typography sx={campaignOrderTitleSx}>{title}</Typography>

      {/* Data Table */}
      <DataTable<OrderedData>
        data={data}
        columns={orderedColumns}
        getRowId={(row) => row.id}
      />

      <Box sx={totalContainerSx}>
        <Typography sx={totalLabelSx}>Total</Typography>
        <Typography sx={totalPriceValueSx}>{total}</Typography>
      </Box>

      {actions && actions.length > 0 && (
        <Box sx={buttonsContainerSx}>{actions}</Box>
      )}
    </Box>
  );
};
